import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react"

import { METRIC_VIEWER_EVENTS, useEventBus } from "@/metrics/registry"

export const TEST_DRAWER_VIEW_MODE = {
  ANNOTATIONS: "annotations",
  MONITORING: "monitoring",
  SIMULATIONS: "simulations",
} as const

export type ViewMode =
  (typeof TEST_DRAWER_VIEW_MODE)[keyof typeof TEST_DRAWER_VIEW_MODE]

export const TEST_DRAWER_TAB_OPTIONS: Array<{
  key: ViewMode
  title: string
}> = [
  {
    key: TEST_DRAWER_VIEW_MODE.ANNOTATIONS,
    title: "Annotations",
  },
  {
    key: TEST_DRAWER_VIEW_MODE.MONITORING,
    title: "Uploaded Conversations",
  },
  {
    key: TEST_DRAWER_VIEW_MODE.SIMULATIONS,
    title: "Simulated Conversations",
  },
]

interface RequestedFilters {
  agreement?: "all" | "agree" | "disagree"
  includeUnreviewed?: boolean
  includeWithoutMetricOutput?: boolean
}

interface TestDrawerContextType {
  // Core drawer state
  isOpen: boolean
  viewMode: ViewMode
  selectedSimulationId: string | null
  /** When set, the sim review table should show only these sims. */
  focusedSimulationIds: string[] | null
  /** Filters requested by a stats card click; consumed once by AnnotationsView. */
  requestedFilters: RequestedFilters | null
  /**
   * Conversations selected for the next test run. `null` means untouched, which
   * defaults to the first MAX_TEST_CONVERSATIONS filtered sims.
   */
  selectedRunIds: Set<string> | null

  // Core actions
  handleClose: () => void
  setViewMode: (mode: ViewMode) => void
  handleRowClick: (simulationId: string) => void
  handleBackToList: () => void
  clearFocusedSimulationIds: () => void
  consumeRequestedFilters: () => RequestedFilters | null
  setSelectedRunIds: (ids: Set<string> | null) => void
}

const TestDrawerContext = createContext<TestDrawerContextType | undefined>(
  undefined,
)

interface TestDrawerProviderProps {
  children: ReactNode
  defaultViewMode?: ViewMode
  initiallyOpen?: boolean
  onClose?: () => void
}

export const TestDrawerProvider: React.FC<TestDrawerProviderProps> = ({
  children,
  defaultViewMode = TEST_DRAWER_VIEW_MODE.ANNOTATIONS,
  initiallyOpen = false,
  onClose,
}) => {
  const [isOpen, setIsOpen] = useState(initiallyOpen)
  const [viewMode, setViewModeState] = useState<ViewMode>(defaultViewMode)
  const [selectedSimulationId, setSelectedSimulationId] = useState<
    string | null
  >(null)
  const [focusedSimulationIds, setFocusedSimulationIds] = useState<
    string[] | null
  >(null)
  const [requestedFilters, setRequestedFilters] =
    useState<RequestedFilters | null>(null)
  const [selectedRunIds, setSelectedRunIdsState] = useState<Set<string> | null>(
    null,
  )

  // Listen for open event — optionally receives a sim-id filter from the confusion matrix
  // or requested filters from stats cards.
  useEventBus(METRIC_VIEWER_EVENTS.TEST_METRIC_OPEN, (data) => {
    setIsOpen(true)
    setFocusedSimulationIds(data?.focusedSimulationIds ?? null)
    setRequestedFilters(data?.requestedFilters ?? null)
    if (data?.viewMode) {
      setViewModeState(data.viewMode)
    }
    if (data?.selectedSimulationId) {
      setSelectedSimulationId(data.selectedSimulationId)
    }
  })

  const clearFocusedSimulationIds = useCallback(() => {
    setFocusedSimulationIds(null)
  }, [])

  const consumeRequestedFilters = useCallback(() => {
    const current = requestedFilters
    setRequestedFilters(null)
    return current
  }, [requestedFilters])

  const handleClose = useCallback(() => {
    setIsOpen(false)
    setFocusedSimulationIds(null)
    setRequestedFilters(null)
    setSelectedRunIdsState(null)
    onClose?.()
  }, [onClose])

  // Included-set model: `null` means untouched (defaults to the first
  // MAX_TEST_CONVERSATIONS filtered sims); a non-null set is an explicit choice.
  const setSelectedRunIds = useCallback((ids: Set<string> | null) => {
    setSelectedRunIdsState(ids === null ? null : new Set(ids))
  }, [])

  const handleRowClick = useCallback((simulationId: string) => {
    setSelectedSimulationId(simulationId)
  }, [])

  const handleBackToList = useCallback(() => {
    setSelectedSimulationId(null)
  }, [])

  const setViewMode = useCallback((mode: ViewMode) => {
    setViewModeState(mode)
    setSelectedSimulationId(null)
  }, [])

  const value = useMemo(
    () => ({
      isOpen,
      viewMode,
      selectedSimulationId,
      focusedSimulationIds,
      requestedFilters,
      selectedRunIds,
      handleClose,
      setViewMode,
      handleRowClick,
      handleBackToList,
      clearFocusedSimulationIds,
      consumeRequestedFilters,
      setSelectedRunIds,
    }),
    [
      isOpen,
      viewMode,
      selectedSimulationId,
      focusedSimulationIds,
      requestedFilters,
      selectedRunIds,
      handleClose,
      setViewMode,
      handleRowClick,
      handleBackToList,
      clearFocusedSimulationIds,
      consumeRequestedFilters,
      setSelectedRunIds,
    ],
  )

  return (
    <TestDrawerContext.Provider value={value}>
      {children}
    </TestDrawerContext.Provider>
  )
}

export const useTestDrawer = (): TestDrawerContextType => {
  const context = useContext(TestDrawerContext)
  if (!context) {
    throw new Error("useTestDrawer must be used within TestDrawerProvider")
  }
  return context
}
